import type { ReactElement, ReactNode } from 'react';
import { useEffect, useId, useRef, useState } from 'react';
import { LanguageSwitcher, t } from '@fetmode/i18n';
import { useAuth } from '@fetmode/app-core';
import { ColorModeToggle } from '@fetmode/theme';
import { Link, useLocation } from 'react-router-dom';

import { RoxyListenToggle } from '../../../features/roxy/RoxyListenToggle.js';
import { StudioSettingsNav } from '../../../features/settings/StudioSettingsNav.js';
import { useStudioModel } from '../../../features/studio/index.js';
import { StudioGlobalChat } from '../StudioGlobalChat/index.js';
import { StudioSiteFooter } from '../StudioSiteFooter/index.js';
import { ChatAvailabilitySelect } from './ChatAvailabilitySelect.js';
import './AppShell.css';

const disabledNavStyle = {
  color: 'var(--fm-color-text-muted)',
  opacity: 0.55,
  cursor: 'not-allowed',
  textDecoration: 'none',
} as const;

function LockedNavItem({ label }: { label: string }): ReactElement {
  return (
    <span title={t('common.studio.live.navLockedHint')} style={disabledNavStyle} aria-disabled="true">
      {label}
    </span>
  );
}

function StudioOverflowLinks({
  isNavLocked,
  settingsCurrent,
  overviewTo,
  onNavigate,
  logout,
  showChat,
  itemClassName,
}: {
  isNavLocked: boolean;
  settingsCurrent: boolean;
  overviewTo: string;
  onNavigate?: () => void;
  logout: () => void;
  showChat: boolean;
  itemClassName?: string;
}): ReactElement {
  return (
    <>
      {showChat ? (
        <li className={itemClassName}>
          <ChatAvailabilitySelect />
        </li>
      ) : null}
      <li className={itemClassName}>
        {isNavLocked ? (
          <LockedNavItem label={t('common.studio.nav.content')} />
        ) : (
          <Link to="/content" onClick={onNavigate}>
            {t('common.studio.nav.content')}
          </Link>
        )}
      </li>
      <li className={itemClassName}>
        {isNavLocked ? (
          <LockedNavItem label={t('common.studio.nav.wallet')} />
        ) : (
          <Link to="/wallet" onClick={onNavigate}>
            {t('common.studio.nav.wallet')}
          </Link>
        )}
      </li>
      <li className={itemClassName}>
        <RoxyListenToggle />
      </li>
      <li className={itemClassName}>
        {isNavLocked ? (
          <LockedNavItem label={t('common.studio.nav.settings')} />
        ) : (
          <Link to={overviewTo} aria-current={settingsCurrent ? 'page' : undefined} onClick={onNavigate}>
            {t('common.studio.nav.settings')}
          </Link>
        )}
      </li>
      <li className={itemClassName}>
        <button type="button" className="app-shell__sign-out" onClick={logout}>
          {t('common.studio.nav.signOut')}
        </button>
      </li>
    </>
  );
}

function MenuPrefs(): ReactElement {
  return (
    <li className="app-shell__menu-prefs">
      <LanguageSwitcher variant="compact" />
      <ColorModeToggle
        labelToDark={t('common.theme.toDark')}
        labelToLight={t('common.theme.toLight')}
      />
    </li>
  );
}

export function AppShell({ children }: { children: ReactNode }): ReactElement {
  const { user, logout } = useAuth();
  const { isNavLocked, profile } = useStudioModel();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuId = useId();
  const menuWrapRef = useRef<HTMLDivElement>(null);

  const isStreamPage = location.pathname === '/live' || location.pathname.startsWith('/live/');
  const isRecruitPage = location.pathname === '/';
  const isOnboardingFlow = isRecruitPage || location.pathname === '/onboarding';
  const isRoxyPage = location.pathname === '/roxy';
  const canBroadcast = Boolean(profile?.canBroadcast);
  const applicationIncomplete = Boolean(user) && !canBroadcast;
  const showSideNav = Boolean(user) && canBroadcast && !isStreamPage && !isOnboardingFlow;
  const overviewTo = '/settings/overview';
  const brandTo = user && canBroadcast ? overviewTo : applicationIncomplete ? '/onboarding' : '/';
  const settingsCurrent =
    location.pathname === '/settings' ||
    location.pathname.startsWith('/settings/') ||
    isRoxyPage;

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!menuOpen) {
      return;
    }
    function onPointerDown(event: PointerEvent): void {
      if (menuWrapRef.current && !menuWrapRef.current.contains(event.target as Node)) {
        setMenuOpen(false);
      }
    }
    function onKeyDown(event: KeyboardEvent): void {
      if (event.key === 'Escape') {
        setMenuOpen(false);
      }
    }
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [menuOpen]);

  const overflowProps = {
    isNavLocked,
    settingsCurrent,
    overviewTo,
    logout,
    showChat: Boolean(profile),
  };

  const brandNode = isNavLocked ? (
    <span className="app-shell__brand app-shell__brand--static">
      <img src="/brand/logo.png" alt="" width={32} height={32} aria-hidden />
      <strong className="app-shell__brand-wordmark">{t('common.studio.app.name')}</strong>
    </span>
  ) : (
    <Link to={brandTo} className="app-shell__brand">
      <img src="/brand/logo.png" alt="" width={32} height={32} aria-hidden />
      <strong className="app-shell__brand-wordmark">{t('common.studio.app.name')}</strong>
    </Link>
  );

  const primaryCta =
    user && !applicationIncomplete ? (
      <Link
        to="/live"
        className="app-shell__broadcast"
        aria-current={isStreamPage ? 'page' : undefined}
      >
        {t('common.studio.nav.broadcast')}
      </Link>
    ) : user && applicationIncomplete ? (
      <Link
        to="/onboarding"
        className="app-shell__broadcast"
        aria-current={location.pathname === '/onboarding' ? 'page' : undefined}
      >
        {t('common.studio.nav.application')}
      </Link>
    ) : (
      <Link
        to="/"
        className="app-shell__broadcast"
        aria-current={isRecruitPage ? 'page' : undefined}
      >
        {t('common.studio.nav.broadcast')}
      </Link>
    );

  return (
    <div className="app-shell">
      <a href="#main" className="skip-link">
        Skip to main content
      </a>
      {isNavLocked ? (
        <div className="app-shell__live-banner" role="status">
          {t('common.studio.live.navLockedBanner')}
        </div>
      ) : null}
      <header className="app-shell__header">
        <div className="app-shell__brand-slot">{brandNode}</div>

        <div className="app-shell__menu-slot" ref={menuWrapRef}>
          <button
            type="button"
            className="app-shell__menu-toggle app-shell__mobile-only"
            aria-expanded={menuOpen}
            aria-controls={menuId}
            onClick={() => setMenuOpen((open) => !open)}
          >
            {t('common.nav.menu')}
          </button>
          {menuOpen ? (
            <ul id={menuId} className="app-shell__menu-panel" role="menu">
              {user && !applicationIncomplete ? (
                <StudioOverflowLinks
                  {...overflowProps}
                  onNavigate={() => setMenuOpen(false)}
                />
              ) : null}
              {user && applicationIncomplete ? (
                <li>
                  <button type="button" className="app-shell__sign-out" onClick={logout}>
                    {t('common.studio.nav.signOut')}
                  </button>
                </li>
              ) : null}
              {!user ? (
                <li>
                  <Link to="/login" state={location.state} onClick={() => setMenuOpen(false)}>
                    {t('common.studio.nav.signIn')}
                  </Link>
                </li>
              ) : null}
              <MenuPrefs />
            </ul>
          ) : null}
        </div>

        <nav aria-label="Primary" className="app-shell__nav-wrap">
          <ul className="app-shell__nav">
            {user && !applicationIncomplete ? (
              <>
                <li className="app-shell__desktop-only">
                  <Link
                    to="/live"
                    className="app-shell__broadcast"
                    aria-current={isStreamPage ? 'page' : undefined}
                  >
                    {t('common.studio.nav.broadcast')}
                  </Link>
                </li>
                <StudioOverflowLinks {...overflowProps} itemClassName="app-shell__desktop-only" />
              </>
            ) : null}
            {user && applicationIncomplete ? (
              <>
                <li className="app-shell__desktop-only">
                  <Link
                    to="/onboarding"
                    aria-current={location.pathname === '/onboarding' ? 'page' : undefined}
                  >
                    {t('common.studio.nav.application')}
                  </Link>
                </li>
                <li className="app-shell__desktop-only">
                  <button type="button" className="app-shell__sign-out" onClick={logout}>
                    {t('common.studio.nav.signOut')}
                  </button>
                </li>
              </>
            ) : null}
            {!user ? (
              <>
                <li className="app-shell__desktop-only">
                  <Link
                    to="/"
                    className="app-shell__broadcast"
                    aria-current={isRecruitPage ? 'page' : undefined}
                  >
                    {t('common.studio.nav.broadcast')}
                  </Link>
                </li>
                <li className="app-shell__desktop-only">
                  <Link to="/login" state={location.state}>
                    {t('common.studio.nav.signIn')}
                  </Link>
                </li>
              </>
            ) : null}
          </ul>
        </nav>

        <div className="app-shell__cta-slot app-shell__mobile-only">{primaryCta}</div>

        <div className="app-shell__meta-wrap app-shell__desktop-only">
          <LanguageSwitcher variant="compact" />
          <ColorModeToggle
            labelToDark={t('common.theme.toDark')}
            labelToLight={t('common.theme.toLight')}
          />
        </div>
      </header>
      {menuOpen ? (
        <button
          type="button"
          className="app-shell__menu-backdrop"
          aria-label={t('common.nav.closeMenu')}
          onClick={() => setMenuOpen(false)}
        />
      ) : null}
      <div
        className={[
          'app-shell__body',
          isStreamPage ? 'app-shell__body--stream' : '',
          isRecruitPage ? 'app-shell__body--recruit' : '',
          showSideNav ? 'app-shell__body--with-nav' : '',
        ]
          .filter(Boolean)
          .join(' ')}
      >
        {showSideNav ? <StudioSettingsNav /> : null}
        <main id="main" className="app-shell__main">
          {children}
        </main>
      </div>
      {!isStreamPage ? <StudioSiteFooter /> : null}
      {!isStreamPage && !isRecruitPage && canBroadcast ? <StudioGlobalChat variant="dock" /> : null}
    </div>
  );
}
